/**
 * Baseline Management — history & governance hub.
 * Route: /schedule/baselines
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBaseline, IconRefresh, IconTimeline, IconTrash } from '@tabler/icons-react';
import {
  discardBaselineRevision,
  fetchActiveBaselineRevision,
  fetchBaselineRevisionHistory,
  type BaselineRevisionDetail,
  type BaselineRevisionSummary,
} from '../../api/baselineApis';
import { APP_ROUTES } from '../../navigation/appRoutes';
import { useCanSchedulerAction } from '../../permissions/scheduler';
import { getProjectId } from '../../utils/cookies';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import { groupRevisionsByBaselineIndex } from './logic/baselineHistoryUtils';
import { BaselineActiveCard } from './components/BaselineActiveCard';
import { BaselineRevisionDetailDrawer } from './components/BaselineRevisionDetailDrawer';
import { BaselineRevisionTable } from './components/BaselineRevisionTable';
import { BaselineSlotList } from './components/BaselineSlotList';

type DiscardTarget =
  | { kind: 'revision'; revision: BaselineRevisionSummary }
  | { kind: 'slot'; baselineIndex: number; baselineName: string }
  | null;

type BaselinesPageData = {
  revisions: BaselineRevisionSummary[];
  active: BaselineRevisionSummary | null;
};

/** Share in-flight loads across StrictMode double-mount so each endpoint hits the network once. */
const inflightPageLoads = new Map<string, Promise<BaselinesPageData>>();
const pageDataCache = new Map<string, { at: number; data: BaselinesPageData }>();
const PAGE_DATA_CACHE_MS = 2000;

async function fetchBaselinesPageData(
  projectId: string,
  options?: { force?: boolean },
): Promise<BaselinesPageData> {
  if (!options?.force) {
    const cached = pageDataCache.get(projectId);
    if (cached && Date.now() - cached.at < PAGE_DATA_CACHE_MS) {
      return cached.data;
    }
    const existing = inflightPageLoads.get(projectId);
    if (existing) return existing;
  }

  const promise = (async (): Promise<BaselinesPageData> => {
    const [historyResult, active] = await Promise.all([
      fetchBaselineRevisionHistory({ projectId }, { silent: true }),
      fetchActiveBaselineRevision({ projectId }, { silent: true }),
    ]);
    const revisions =
      historyResult.kind === 'list'
        ? historyResult.revisions
        : historyResult.kind === 'detail'
          ? [historyResult.detail.summary]
          : [];
    const data = {
      revisions,
      active: active?.isDeleted ? null : active,
    };
    pageDataCache.set(projectId, { at: Date.now(), data });
    return data;
  })().finally(() => {
    if (inflightPageLoads.get(projectId) === promise) {
      inflightPageLoads.delete(projectId);
    }
  });

  inflightPageLoads.set(projectId, promise);
  return promise;
}

export default function BaselinesPage() {
  const navigate = useNavigate();
  const projectId = getProjectId() || '';
  const projectDisplayName = getProjectDisplayName();
  const canTaskSetBaseline = useCanSchedulerAction('taskSetBaseline');
  const canTaskEdit = useCanSchedulerAction('taskEdit');
  const canDiscard = canTaskSetBaseline || canTaskEdit;

  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState<BaselineRevisionSummary[]>([]);
  const [activeRevision, setActiveRevision] = useState<BaselineRevisionSummary | null>(null);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<BaselineRevisionDetail | null>(null);
  const [discardTarget, setDiscardTarget] = useState<DiscardTarget>(null);
  const [discarding, setDiscarding] = useState(false);

  const slots = useMemo(() => groupRevisionsByBaselineIndex(revisions), [revisions]);
  const selectedSlot = useMemo(
    () => slots.find((s) => s.baselineIndex === selectedSlotIndex) ?? null,
    [slots, selectedSlotIndex],
  );

  const loadPage = useCallback(async () => {
    if (!projectId) {
      setRevisions([]);
      setActiveRevision(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // force: true so Refresh / post-discard always re-fetch; mount still dedupes via cache/inflight.
      const { revisions: list, active } = await fetchBaselinesPageData(projectId, {
        force: true,
      });
      setRevisions(list);
      setActiveRevision(active);
      setSelectedSlotIndex((prev) => {
        if (prev != null && list.some((r) => r.baselineIndex === prev)) return prev;
        const grouped = groupRevisionsByBaselineIndex(list);
        return grouped[0]?.baselineIndex ?? null;
      });
    } catch (err) {
      console.error('[Baselines] Failed to load history:', err);
      notifications.show({
        title: 'Could not load baselines',
        message: 'Failed to fetch baseline revision history. Try refresh.',
        color: 'red',
      });
      setRevisions([]);
      setActiveRevision(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!projectId) {
        if (!cancelled) {
          setRevisions([]);
          setActiveRevision(null);
          setLoading(false);
        }
        return;
      }
      if (!cancelled) setLoading(true);
      try {
        // Mount/StrictMode: reuse in-flight or brief cache (no force).
        const { revisions: list, active } = await fetchBaselinesPageData(projectId);
        if (cancelled) return;
        setRevisions(list);
        setActiveRevision(active);
        setSelectedSlotIndex((prev) => {
          if (prev != null && list.some((r) => r.baselineIndex === prev)) return prev;
          const grouped = groupRevisionsByBaselineIndex(list);
          return grouped[0]?.baselineIndex ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[Baselines] Failed to load history:', err);
        notifications.show({
          title: 'Could not load baselines',
          message: 'Failed to fetch baseline revision history. Try refresh.',
          color: 'red',
        });
        setRevisions([]);
        setActiveRevision(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const openRevisionDetail = useCallback(
    async (revision: BaselineRevisionSummary) => {
      if (!projectId) return;
      setSelectedRevisionId(revision.id);
      setDetailOpen(true);
      setDetailLoading(true);
      setDetail(null);
      try {
        const result = await fetchBaselineRevisionHistory(
          {
            projectId,
            baselineIndex: revision.baselineIndex,
            revisionNumber: revision.revisionNumber,
          },
          { silent: true },
        );
        if (result.kind === 'detail') {
          setDetail(result.detail);
        } else if (result.kind === 'list' && result.revisions[0]) {
          setDetail({ summary: result.revisions[0], tasks: [] });
        } else {
          setDetail({ summary: revision, tasks: [] });
        }
      } catch (err) {
        console.error('[Baselines] Failed to load revision detail:', err);
        notifications.show({
          title: 'Could not load revision',
          message: 'Failed to fetch revision task snapshot.',
          color: 'red',
        });
        setDetail({ summary: revision, tasks: [] });
      } finally {
        setDetailLoading(false);
      }
    },
    [projectId],
  );

  const confirmDiscard = useCallback(async () => {
    if (!projectId || !discardTarget || discarding) return;
    setDiscarding(true);
    try {
      if (discardTarget.kind === 'revision') {
        await discardBaselineRevision(
          {
            projectId,
            baselineIndex: discardTarget.revision.baselineIndex,
            revisionNumber: discardTarget.revision.revisionNumber,
          },
          { silent: true },
        );
        notifications.show({
          title: 'Revision discarded',
          message: `Revision ${discardTarget.revision.revisionNumber} was discarded.`,
          color: 'teal',
        });
      } else {
        await discardBaselineRevision(
          {
            projectId,
            baselineIndex: discardTarget.baselineIndex,
          },
          { silent: true },
        );
        notifications.show({
          title: 'Baseline slot discarded',
          message: `${discardTarget.baselineName} (slot ${discardTarget.baselineIndex}) was discarded.`,
          color: 'teal',
        });
      }
      setDiscardTarget(null);
      setDetailOpen(false);
      setDetail(null);
      setSelectedRevisionId(null);
      await loadPage();
    } catch (err) {
      console.error('[Baselines] Discard failed:', err);
    } finally {
      setDiscarding(false);
    }
  }, [projectId, discardTarget, discarding, loadPage]);

  const goToGantt = () => {
    navigate(APP_ROUTES.schedule.tasks);
  };

  if (!projectId) {
    return (
      <Box p="xl">
        <Text c="red">No project selected.</Text>
      </Box>
    );
  }

  return (
    <Box
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--app-shell-bg, var(--mantine-color-body))',
      }}
    >
      <Box
        px="md"
        py="md"
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          background: 'var(--app-toolbar-bg, var(--mantine-color-body))',
          flexShrink: 0,
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Group gap={8}>
              <IconBaseline size={20} stroke={1.5} />
              <Text fw={600} style={{ fontSize: 18, letterSpacing: '-0.18px', lineHeight: 1.3 }}>
                Baselines
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              {projectDisplayName
                ? `History and governance for ${projectDisplayName}`
                : 'History and governance for approved slots and revisions'}
            </Text>
          </Stack>
          <Group gap="sm">
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void loadPage()}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              size="xs"
              color="brand"
              leftSection={<IconTimeline size={14} />}
              onClick={goToGantt}
            >
              Open Gantt
            </Button>
          </Group>
        </Group>
      </Box>

      <Box px="md" pt="md" style={{ flexShrink: 0 }}>
        {activeRevision ? (
          <BaselineActiveCard revision={activeRevision} onManageOnGantt={goToGantt} />
        ) : (
          <Box
            style={{
              borderRadius: 12,
              border: '1px dashed var(--mantine-color-default-border)',
              padding: '12px 16px',
            }}
          >
            <Text size="sm" c="dimmed">
              No open draft / submitted / rejected revision. Create or manage baselines from
              Gantt.
            </Text>
          </Box>
        )}
      </Box>

      <Box
        px="md"
        py="md"
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 12,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Group justify="center" align="center" style={{ flex: 1 }}>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading baselines…
            </Text>
          </Group>
        ) : (
          <>
            <Box
              style={{
                width: 280,
                flexShrink: 0,
                borderRadius: 12,
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-body)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <Box
                px="sm"
                py="xs"
                style={{
                  flexShrink: 0,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-body)',
                }}
              >
                <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
                  Slots
                </Text>
              </Box>
              <Box style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <BaselineSlotList
                  slots={slots}
                  selectedIndex={selectedSlotIndex}
                  onSelect={(index) => {
                    setSelectedSlotIndex(index);
                    setSelectedRevisionId(null);
                  }}
                />
              </Box>
            </Box>

            <Box
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                borderRadius: 12,
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--mantine-color-body)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Group
                justify="space-between"
                align="center"
                px="md"
                py="sm"
                style={{
                  flexShrink: 0,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  borderBottom: '1px solid var(--mantine-color-default-border)',
                  background: 'var(--mantine-color-body)',
                }}
              >
                <Stack gap={2}>
                  <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
                    Revisions
                  </Text>
                  <Text size="sm" fw={600}>
                    {selectedSlot
                      ? selectedSlot.baselineName
                      : 'Select a baseline slot'}
                  </Text>
                </Stack>
                {selectedSlot && canDiscard ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={12} />}
                    disabled={discarding}
                    onClick={() =>
                      setDiscardTarget({
                        kind: 'slot',
                        baselineIndex: selectedSlot.baselineIndex,
                        baselineName: selectedSlot.baselineName,
                      })
                    }
                  >
                    Discard slot
                  </Button>
                ) : null}
              </Group>
              <Box style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {selectedSlot ? (
                  <BaselineRevisionTable
                    revisions={selectedSlot.revisions}
                    selectedRevisionId={selectedRevisionId}
                    canDiscard={canDiscard}
                    discarding={discarding}
                    onSelect={(rev) => void openRevisionDetail(rev)}
                    onDiscardRevision={(rev) =>
                      setDiscardTarget({ kind: 'revision', revision: rev })
                    }
                  />
                ) : (
                  <Text size="sm" c="dimmed" p="md">
                    Select a slot to view its revision history.
                  </Text>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <BaselineRevisionDetailDrawer
        opened={detailOpen}
        loading={detailLoading}
        detail={detail}
        onClose={() => {
          setDetailOpen(false);
          setSelectedRevisionId(null);
        }}
      />

      <Modal
        opened={discardTarget != null}
        onClose={() => {
          if (!discarding) setDiscardTarget(null);
        }}
        title={
          discardTarget?.kind === 'slot' ? 'Discard baseline slot?' : 'Discard revision?'
        }
        centered
        closeOnClickOutside={!discarding}
        closeOnEscape={!discarding}
      >
        <Stack gap="md">
          {discardTarget?.kind === 'slot' ? (
            <Text size="sm">
              This permanently discards <strong>{discardTarget.baselineName}</strong> (slot{' '}
              {discardTarget.baselineIndex}), including all revisions and approved task
              baselines for that slot. This cannot be undone.
            </Text>
          ) : discardTarget?.kind === 'revision' ? (
            <Text size="sm">
              Discard revision <strong>#{discardTarget.revision.revisionNumber}</strong> (
              {discardTarget.revision.status}) for slot {discardTarget.revision.baselineIndex}?
            </Text>
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              disabled={discarding}
              onClick={() => setDiscardTarget(null)}
            >
              Cancel
            </Button>
            <Button color="red" loading={discarding} onClick={() => void confirmDiscard()}>
              Discard
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
